import { Component } from '@angular/core';
import { WizardPageComponent } from '../../../../../core/wizard/wizard-page-component';
import type { DemographicDetailsWizard } from '../demographic-details-wizard';
import { RegionType } from '../../../../../core/location/region-type.enum';
import { Observable, of } from 'rxjs';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, debounceTime, map, switchMap, take, tap } from 'rxjs/operators';
import { formatAsCurrency } from '../../../../../core/util/string-utils';
import { LocationService } from '../../../../../core/location/location.service';
import { ToastService } from '../../../../../core/toast/toast.service';
import { AppState } from 'src/app/app-state';
import { Store as NgrxStore } from '@ngrx/store';
import { DetailState, selectDetailState } from '../../../../detail-state';
import { FeatureCollection } from '../../../../../core/models';
import { DemographicsService } from '../../../../../core/depot/demographics.service';
import { DemographicDataView } from '../../../../../core/depot/demographic-data-view';
import { round } from 'src/app/core/util/math-utils';
import { CircleRequest } from 'src/app/core/shared/shape/circle-request';
import { ReportService } from 'src/app/core/service/report.service';
import { HitTrackingService } from 'src/app/core/hit-tracking/hit-tracking.service';
import { USER_TRACKING_ACTION_DESCRIPTIONS, USER_TRACKING_ACTIONS, USER_TRACKING_SOURCES } from 'src/app/core/constants/user-tracking.constants';

@Component({
  standalone: false,
  selector: 'mtn-demographic-details-page',
  templateUrl: './demographic-details-page.component.html',
  styleUrls: ['./demographic-details-page.component.scss']
})
export class DemographicDetailsPageComponent extends WizardPageComponent<DemographicDetailsWizard> {

  key = 'demographic-details';

  private readonly VALIDATORS_DRIVE_TIME = [Validators.min(1), Validators.max(20)];
  private readonly VALIDATORS_RING = [Validators.min(1), Validators.max(10)];

  public readonly EDUCATION_DESCRIPTION = 'These values represent the population\'s highest-attained level of education.';

  demographics: DemographicDataView;
  formatAsCurrency = formatAsCurrency;
  isLargeArea = false;
  isLoading = false;
  pop2yrChange: string;
  pop5yrProjection: string;
  RegionType = RegionType;
  round = round;
  marketName: any;

  constructor(private demographicsService: DemographicsService,
    private locationService: LocationService,
    private reportService: ReportService,
    private ngrxStore: NgrxStore<AppState>,
    private toaster: ToastService,
    private hitTrackingService: HitTrackingService) {
    super();
    this.title = 'Demographic Details';
    this.closeButtonText = 'Close';
    this.form = new UntypedFormGroup({
      region: new UntypedFormControl(RegionType.DRIVE_TIME),
      driveTimeMinutes: new UntypedFormControl(7, [...this.VALIDATORS_DRIVE_TIME, Validators.required]),
      ringMiles: new UntypedFormControl(3, this.VALIDATORS_RING)
    });
    this.subscribeToDetailState();
  }

  onLoad(): Observable<any> {
    this.setDemographics(this.wizard.model.demographics);
    this.form.patchValue(this.wizard.model);
    this.subscribeToFormChanges();
    this.trackDemographicDetailsLoaded();
    return super.onLoad();
  }

  private subscribeToDetailState(): void {
    this.addSubscription(
      selectDetailState(this.ngrxStore)
        .subscribe((state: DetailState) => {
          this.marketName = state.marketArea.getFeature().properties['name'];
        })
    );
  }

  private trackDemographicDetailsLoaded(): void {
    const store = this.wizard?.model?.store;
    const location = store?.space?.location;
    const shoppingCenter = store?.space?.shoppingCenter;

    const metadata = {
      storeId: store?.uuid,
      storeName: store?.name,
      addressLine1: location?.addressLine1,
      city: location?.city,
      cbsa: this.marketName,
      state: location?.state,
      postalCode: location?.postalCode,
      county: location?.county,
      shoppingCenterName: shoppingCenter?.name,
      landlord: shoppingCenter?.ownerCompany?.name
    };

    this.hitTrackingService.hitTheUserTracking(
      USER_TRACKING_SOURCES.DEMOGRAPHIC_DETAILS_PAGE,
      metadata,
      USER_TRACKING_ACTIONS.DEMOGRAPHIC_DETAILS_LOADED,
      USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.DEMOGRAPHIC_DETAILS_LOADED]
    ).subscribe();

  }


  private getSelectedRegionShape(regionType: RegionType): Observable<FeatureCollection> {
    this.isLargeArea = false;
    switch (regionType) {
      case RegionType.DRIVE_TIME:
        const minutes = this.form.get('driveTimeMinutes').value;

        if (minutes === 7) {
          return selectDetailState(this.ngrxStore)
            .pipe(take(1),
              map((state: DetailState) => state.defaultDriveTime,));
        } else {
          if (minutes > 10) {
            this.isLargeArea = true;
          }
          return this.locationService.findOnesIsochrone(this.wizard.model.store.space.location.uuid, minutes);
        }
      case RegionType.MARKET_AREA:
        this.isLargeArea = true;
        return of(this.wizard.model.marketArea);
      case RegionType.RING:
        const radius = this.form.get('ringMiles').value;
        this.isLargeArea = radius > 5;
        /*const circle = new google.maps.Circle({
          center: this.store.space.location.getPointAsLatLng(),
          radius: radius
        });*/
        const circleRequest: CircleRequest = new CircleRequest();
        circleRequest.point = {
          type: 'Point',
          coordinates: [this.wizard.model.store.space.location.getPointLongitude(), this.wizard.model.store.space.location.getPointLatitude()]
        };
        circleRequest.distanceMiles = radius;

        return this.reportService.createOne(circleRequest);
    }
  }

  private loadDemographics(): void {
    if (!this.isLoading) {
      this.isLoading = true;
      this.demographics = null;

      const formValue = this.form.getRawValue();
      const regionType = formValue.region;
      const driveMinutes = formValue.driveTimeMinutes;
      const ringMiles = formValue.ringMiles;


      if (
        (regionType === RegionType.DRIVE_TIME && (driveMinutes < 1 || driveMinutes > 20)) ||
        (regionType === RegionType.RING && (ringMiles < 1 || ringMiles > 10))
      ) {
        this.isLoading = false;
        return;
      }

      const regionDescription =
        regionType === RegionType.DRIVE_TIME
          ? `${driveMinutes} minutes`
          : regionType === RegionType.RING
            ? `${ringMiles} miles`
            : 'Market Area';

      const store = this.wizard?.model?.store;
      const location = store?.space?.location;
      const shoppingCenter = store?.space?.shoppingCenter;

      const trackingMetadata = {
        storeId: store?.uuid,
        storeName: store?.name,
        addressLine1: location?.addressLine1,
        city: location?.city,
        cbsa: this.marketName,
        state: location?.state,
        postalCode: location?.postalCode,
        county: location?.county,
        shoppingCenterName: shoppingCenter?.name,
        landlord: shoppingCenter?.ownerCompany?.name,
        regionType,
        regionDescription
      };


      this.hitTrackingService.hitTheUserTracking(
        USER_TRACKING_SOURCES.DEMOGRAPHIC_DETAILS_PAGE,
        trackingMetadata,
        USER_TRACKING_ACTIONS.DEMOGRAPHIC_REGION_CHANGED,
        USER_TRACKING_ACTION_DESCRIPTIONS[USER_TRACKING_ACTIONS.DEMOGRAPHIC_REGION_CHANGED]
      ).subscribe();

      this.getSelectedRegionShape(formValue.region)
        .pipe(
          catchError((err) => {
            console.error(err);
            this.toaster.error("Uh-oh! We weren't able to load the Demographic Profile for this store. If the problem persists, please contact an administrator for assistance.");
            this.isLoading = false;
            return of(null);
          }),
          switchMap((regionGeoJson: FeatureCollection) => {
            return this.demographicsService.findDemographicsForFeatureCollection(regionGeoJson)
              .pipe(tap((result: DemographicDataView) => {
                setTimeout(() => {
                  this.setDemographics(result);
                  this.isLoading = false;
                }, 300);
              }));
          })
        ).subscribe();
    }
  }

  private setDemographics(demographics: DemographicDataView): void {
    this.demographics = demographics;

    const change = this.demographics.populationHistoricalGrowth2Year;
    const changeSign = change > 0 ? '+' : '';
    this.pop2yrChange = `${changeSign}${(change * 100).toFixed(2)}%`;

    const projection = this.demographics.populationProjectedGrowth5Year;
    const projectionSign = projection > 0 ? '+' : '';
    this.pop5yrProjection = `${projectionSign}${(projection * 100).toFixed(2)}%`;
  }

  private subscribeToFormChanges(): void {
    this.addSubscription(
      this.form.get('region').valueChanges
        .subscribe((value: RegionType) => {
          //Update driveTimeMinutes validators
          const driveTimeMinutesValidators = [...this.VALIDATORS_DRIVE_TIME];
          if (value === RegionType.DRIVE_TIME) {
            driveTimeMinutesValidators.push(Validators.required)
          }
          this.form.get('driveTimeMinutes').setValidators(driveTimeMinutesValidators);

          //Update ringMiles validators
          const ringMilesValidators = [...this.VALIDATORS_RING];
          if (value === RegionType.RING) {
            ringMilesValidators.push(Validators.required);
          }
          this.form.get('ringMiles').setValidators(ringMilesValidators);

          this.form.updateValueAndValidity();
        })
    );

    this.addSubscription(
      this.form.valueChanges
        .pipe(debounceTime(300))
        .subscribe(() => {
          if (this.form.dirty && this.form.valid) {
            this.loadDemographics();
          }
        })
    );
  }

}
